import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ChatMonitor from "@/components/admin/ChatMonitor";
import { Star, Crown, MessageSquare, Shuffle, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const tone = { voting: "amber", forming: "amber", awaiting_confirmation: "amber", confirmed: "green", completed: "gray", canceled: "red" };

function GroupDialog({ groupId, onClose, onMonitor }) {
    const qc = useQueryClient();
    const { data } = useQuery({ queryKey: ["admin-group", groupId], queryFn: async () => (await api.get(`/admin/groups/${groupId}`)).data, enabled: !!groupId });
    if (!data) return null;
    const g = data.group;
    const win = (g.suggested_windows || [])[0];

    const reassign = async () => {
        try { const r = await api.post(`/admin/outings/${data.outing_id}/reassign-host`); toast.success(`Reservation reassigned to ${r.data.host}.`); qc.invalidateQueries({ queryKey: ["admin-group", groupId] }); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    return (
        <Dialog open={!!groupId} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="group-detail-dialog">
                <DialogHeader><DialogTitle>Group details</DialogTitle><DialogDescription>Members, activity votes and status for this group.</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <div><span className="text-zinc-500">Group ID:</span> <span className="font-mono text-xs">{g.id.slice(0, 8)}</span></div>
                    <div><span className="text-zinc-500">Market:</span> {g.market}</div>
                    <div><span className="text-zinc-500">Status:</span> <Badge tone={tone[g.status]}>{g.status}</Badge></div>
                    <div><span className="text-zinc-500">Sizes:</span> min {g.min_size} / target {g.target_size}</div>
                    <div><span className="text-zinc-500">Members:</span> {data.member_count} ({data.confirmed_count} confirmed)</div>
                    {win && <div><span className="text-zinc-500">Proposed time:</span> {win.date || "TBD"} {win.start}–{win.end}</div>}
                </div>

                {data.outing_id ? (
                    <div className="rounded bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="group-outing-link">
                        <p>Finalized into outing: <span className="font-600">{data.outing_label}</span></p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" data-testid="group-monitor-chat" onClick={() => onMonitor(data.outing_id, data.outing_label)}>
                                <MessageSquare className="mr-1 h-4 w-4" /> Monitor chat
                            </Button>
                            {data.reservation && data.reservation.status !== "booked" && (
                                <Button size="sm" variant="outline" data-testid="group-reassign-host" onClick={reassign}>
                                    <Shuffle className="mr-1 h-4 w-4" /> Reassign host
                                </Button>
                            )}
                        </div>
                        {data.reservation && <p className="mt-1 text-xs text-emerald-700">Reservation host: {data.reservation.host_temp_emoji} {data.reservation.host_temp_name} · {data.reservation.status}</p>}
                    </div>
                ) : (
                    <p className="text-xs text-zinc-400">This group hasn't finalized into an outing yet — chat &amp; reservation actions appear once it's confirmed.</p>
                )}

                <div>
                    <p className="mb-2 text-sm font-600">Activity options &amp; votes</p>
                    <div className="space-y-1">
                        {data.activities.length === 0 && <p className="text-sm text-zinc-500">No activity options.</p>}
                        {data.activities.map((a) => (
                            <div key={a.id} data-testid={`group-activity-${a.id}`} className="flex items-center justify-between rounded border border-zinc-200 px-3 py-2 text-sm">
                                <span>{a.emoji} {a.name}</span>
                                <span className="flex items-center gap-3 text-xs text-zinc-500">
                                    <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {a.first_choice_votes} first</span>
                                    <span>{a.total_votes} total</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                <div>
                    <p className="mb-2 text-sm font-600">Members ({data.members.length})</p>
                    <div className="rounded-lg border border-zinc-200 overflow-x-auto">
                        <Table>
                            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>City</TableHead><TableHead>First choice</TableHead><TableHead>Confirmed</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {data.members.map((m) => (
                                    <TableRow key={m.user_id} data-testid={`group-member-${m.user_id}`}>
                                        <TableCell className="font-500">
                                            <span className="flex items-center gap-1">
                                                {m.is_requester && <Crown className="h-3.5 w-3.5 text-amber-500" title="Requester" />}
                                                {m.name}
                                            </span>
                                            <span className="block text-xs text-zinc-400">{m.email}</span>
                                        </TableCell>
                                        <TableCell>{m.city || "—"}</TableCell>
                                        <TableCell className="text-zinc-500">{m.first_choice || "—"}</TableCell>
                                        <TableCell><Badge tone={m.confirmed ? "green" : "gray"}>{m.confirmed ? "yes" : "no"}</Badge></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function Groups() {
    const [selected, setSelected] = useState(null);
    const [monitor, setMonitor] = useState(null);
    const [q, setQ] = useState("");
    const [market, setMarket] = useState("");
    const [sort, setSort] = useState({ key: "event_date", dir: "desc" });
    const { data } = useQuery({ queryKey: ["admin-groups"], queryFn: async () => (await api.get("/admin/groups")).data });

    const markets = useMemo(() => Array.from(new Set((data || []).map((g) => g.market).filter(Boolean))).sort(), [data]);
    const sortVal = (g, key) => {
        if (key === "market") return (g.market || "").toLowerCase();
        if (key === "status") return (g.status || "").toLowerCase();
        if (key === "event_date") return g.event_date || "";
        if (key === "member_count" || key === "confirmed_count") return g[key] ?? 0;
        return (g[key] || "").toString().toLowerCase();
    };
    const rows = useMemo(() => {
        let arr = [...(data || [])];
        if (market) arr = arr.filter((g) => g.market === market);
        if (q.trim()) {
            const t = q.trim().toLowerCase();
            arr = arr.filter((g) => (g.market || "").toLowerCase().includes(t) || (g.id || "").toLowerCase().includes(t));
        }
        arr.sort((a, b) => {
            const av = sortVal(a, sort.key), bv = sortVal(b, sort.key);
            if (av < bv) return sort.dir === "asc" ? -1 : 1;
            if (av > bv) return sort.dir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [data, q, market, sort]);
    const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "event_date" ? "desc" : "asc" }));
    const SortHead = ({ k, children }) => (
        <TableHead>
            <button type="button" data-testid={`groups-sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 font-600 hover:text-zinc-900">
                {children}
                {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />}
            </button>
        </TableHead>
    );

    return (
        <div>
            <AdminHeader title="Groups" subtitle="Potential and confirmed activity groups. Click a row to see members, votes and details." />
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="flex flex-1 min-w-[220px] items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 py-2">
                    <Search className="h-4 w-4 text-zinc-400" />
                    <input data-testid="groups-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by market or group ID" className="w-full bg-transparent text-sm outline-none" />
                </div>
                <select data-testid="groups-market-filter" value={market} onChange={(e) => setMarket(e.target.value)}
                    className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none">
                    <option value="">All markets</option>
                    {markets.map((mk) => <option key={mk} value={mk}>{mk}</option>)}
                </select>
                <span className="text-sm text-zinc-500" data-testid="groups-count">{rows.length} group{rows.length === 1 ? "" : "s"}</span>
            </div>
            <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
                <Table>
                    <TableHeader><TableRow><TableHead>Group ID</TableHead><SortHead k="market">Market</SortHead><SortHead k="event_date">Event date</SortHead><SortHead k="status">Status</SortHead><SortHead k="member_count">Members</SortHead><SortHead k="confirmed_count">Confirmed</SortHead><TableHead>Sizes</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {rows.map((g) => (
                            <TableRow key={g.id} data-testid={`group-row-${g.id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => setSelected(g.id)}>
                                <TableCell className="font-mono text-xs">{g.id.slice(0, 8)}</TableCell>
                                <TableCell>{g.market}</TableCell>
                                <TableCell data-testid={`group-date-${g.id}`}>{g.event_date || "TBD"}</TableCell>
                                <TableCell><Badge tone={tone[g.status]}>{g.status}</Badge></TableCell>
                                <TableCell>{g.member_count}</TableCell>
                                <TableCell>{g.confirmed_count}</TableCell>
                                <TableCell className="text-zinc-500 text-xs">min {g.min_size} / target {g.target_size}</TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-zinc-400" data-testid="groups-empty">No groups match your filters.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            {selected && <GroupDialog groupId={selected} onClose={() => setSelected(null)} onMonitor={(id, label) => setMonitor({ id, title: label })} />}
            {monitor && <ChatMonitor outingId={monitor.id} title={monitor.title} onClose={() => setMonitor(null)} />}
        </div>
    );
}
